import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Camera, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Snapshots() {
  const { activeBiz } = useOutletContext();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ search_console_screenshot_url: "", gbp_insights_screenshot_url: "", notes: "" });
  const [diff, setDiff] = useState(null);

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/snapshots`);
      setItems(data);
    } catch {}
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeBiz]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  const capture = async () => {
    setBusy(true);
    try {
      await api.post(`/businesses/${activeBiz.id}/snapshots/capture`);
      toast.success("Snapshot captured");
      reload();
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      search_console_screenshot_url: s.search_console_screenshot_url || "",
      gbp_insights_screenshot_url: s.gbp_insights_screenshot_url || "",
      notes: s.notes || "",
    });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/businesses/${activeBiz.id}/snapshots/${editing}`, form);
      setEditing(null); reload(); toast.success("Saved");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const showDiff = async (sid, prev_sid) => {
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/snapshots/${sid}/diff/${prev_sid}`);
      setDiff(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tiny mb-1">Snapshots</div>
          <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="snap-title">Monthly Before / After</h1>
          <p className="text-zinc-500 mt-1 max-w-2xl">Capture a metrics snapshot on the 1st of every month. Upload Search Console + GBP Insights screenshots for honest, audit-ready proof of work.</p>
        </div>
        <button className="btn-primary" onClick={capture} disabled={busy} data-testid="snap-capture">
          {busy ? <span className="spinner"></span> : <><Camera size={14} weight="fill" /> Capture now</>}
        </button>
      </header>

      <div className="bento p-4 border-l-4 border-[#002FA7] bg-zinc-50 text-sm">
        <strong>Search Console + GBP Insights:</strong> the API integrations need their own Google OAuth approval. Until then, paste the screenshot URL (e.g. upload to Imgur, Cloudinary, S3) into each snapshot — clients will see real before/after images.
      </div>

      {items.length === 0 && (
        <div className="bento p-12 text-center" data-testid="snap-empty">
          <Camera size={28} className="mx-auto text-zinc-400 mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">No snapshots yet</h3>
          <p className="text-zinc-600 max-w-md mx-auto">Hit "Capture now" to take your first metrics snapshot. Each month after, you can show clients exactly what moved.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((s, i) => {
          const prev = items[i + 1];
          return (
            <div key={s.id} className="bento p-5" data-testid={`snap-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-lg">{new Date(s.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
                  <div className="font-mono text-xs text-zinc-500 mt-1">Lead {s.lead_capture_score} · Quality {s.quality_score} · Top-3 {s.top_3_count} · AI vis {s.ai_visibility_rate ?? "—"}%</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary !py-2 !text-[11px]" onClick={() => startEdit(s)} data-testid={`snap-edit-${s.id}`}>Edit screenshots</button>
                  {prev && <button className="btn-dark !py-2 !text-[11px]" onClick={() => showDiff(s.id, prev.id)} data-testid={`snap-diff-${s.id}`}>vs previous <ArrowRight size={12} weight="bold" /></button>}
                </div>
              </div>
              {editing === s.id && (
                <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3">
                  <Field label="Search Console screenshot URL"><input className="input-field" placeholder="https://..." value={form.search_console_screenshot_url} onChange={(e) => setForm({ ...form, search_console_screenshot_url: e.target.value })} data-testid="snap-sc-url" /></Field>
                  <Field label="GBP Insights screenshot URL"><input className="input-field" placeholder="https://..." value={form.gbp_insights_screenshot_url} onChange={(e) => setForm({ ...form, gbp_insights_screenshot_url: e.target.value })} data-testid="snap-gbp-url" /></Field>
                  <Field label="Notes"><textarea className="input-field" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="snap-notes" /></Field>
                  <div className="flex gap-2">
                    <button className="btn-primary !py-2 !text-[11px]" onClick={saveEdit} data-testid="snap-save">Save</button>
                    <button className="btn-secondary !py-2 !text-[11px]" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {(s.search_console_screenshot_url || s.gbp_insights_screenshot_url) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {s.search_console_screenshot_url && (
                    <div><div className="label-tiny mb-1">Search Console</div><img src={s.search_console_screenshot_url} alt="Search Console" className="w-full border border-zinc-200" data-testid={`snap-sc-img-${s.id}`} /></div>
                  )}
                  {s.gbp_insights_screenshot_url && (
                    <div><div className="label-tiny mb-1">GBP Insights</div><img src={s.gbp_insights_screenshot_url} alt="GBP Insights" className="w-full border border-zinc-200" data-testid={`snap-gbp-img-${s.id}`} /></div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {diff && (
        <div className="bento p-6 space-y-4" data-testid="snap-diff-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl font-bold">Before / After</h3>
            <button className="btn-ghost text-xs" onClick={() => setDiff(null)}>Close</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <DiffStat label="Lead Score" cur={diff.current.lead_capture_score} prev={diff.previous.lead_capture_score} />
            <DiffStat label="Quality" cur={diff.current.quality_score} prev={diff.previous.quality_score} />
            <DiffStat label="Top-3" cur={diff.current.top_3_count} prev={diff.previous.top_3_count} />
            <DiffStat label="Top-10" cur={diff.current.top_10_count} prev={diff.previous.top_10_count} />
            <DiffStat label="AI Visibility" cur={diff.current.ai_visibility_rate} prev={diff.previous.ai_visibility_rate} suffix="%" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SidebySide label="Search Console" prev={diff.previous.search_console_screenshot_url} cur={diff.current.search_console_screenshot_url} />
            <SidebySide label="GBP Insights" prev={diff.previous.gbp_insights_screenshot_url} cur={diff.current.gbp_insights_screenshot_url} />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block space-y-2"><span className="label-tiny block">{label}</span>{children}</label>;
}

function DiffStat({ label, cur, prev, suffix = "" }) {
  if (cur == null || prev == null) return (
    <div className="bento p-3"><div className="label-tiny">{label}</div><div className="font-mono text-zinc-400 mt-1">—</div></div>
  );
  const delta = cur - prev;
  const color = delta > 0 ? "text-[#22C55E]" : delta < 0 ? "text-[#EF4444]" : "text-zinc-500";
  return (
    <div className="bento p-3">
      <div className="label-tiny">{label}</div>
      <div className="font-display text-2xl font-black tabular-nums mt-1">{cur}{suffix}</div>
      <div className={`font-mono text-xs ${color}`}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}{suffix} vs prev</div>
    </div>
  );
}

function SidebySide({ label, prev, cur }) {
  if (!prev && !cur) return null;
  return (
    <div className="space-y-2">
      <div className="label-tiny">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        <div><div className="text-[10px] font-mono text-zinc-500 mb-1">BEFORE</div>{prev ? <img src={prev} alt="" className="w-full border border-zinc-200" /> : <div className="bg-zinc-100 h-32 flex items-center justify-center text-xs text-zinc-400">no upload</div>}</div>
        <div><div className="text-[10px] font-mono text-zinc-500 mb-1">AFTER</div>{cur ? <img src={cur} alt="" className="w-full border border-zinc-200" /> : <div className="bg-zinc-100 h-32 flex items-center justify-center text-xs text-zinc-400">no upload</div>}</div>
      </div>
    </div>
  );
}
